import type { RacePhase } from '@runkr/schema'
import type { LeaveReason } from '../net/leaveReason'

/**
 * The screens of an online session (PHASE1 T1.8). Practice is its own page (`?practice`).
 * `profile` (T2.4, D-048), `settings` (PHASE3 T3.7) and `shop` (T3.9a) open from the menu only
 * and go back to it; no room exists there.
 */
export type Screen = 'menu' | 'lobby' | 'race' | 'results' | 'profile' | 'settings' | 'shop'

export type JoinKind = 'public' | 'private' | 'code'

export interface Flow {
  screen: Screen
  /** A join in progress: the lobby shows "Finding a race…" until the room's first state. */
  joining: JoinKind | null
  /** Shown inline on the menu. */
  error: LeaveReason | null
  /** The room is gone (results stay up after the server closes it). */
  closed: boolean
}

export type FlowEvent =
  /** The player pressed Play / Private room / Join, or Play Again on the results. */
  | { type: 'join'; kind: JoinKind }
  /** A room state arrived with this phase. */
  | { type: 'phase'; phase: RacePhase }
  /** The client has no room and is not retrying; `reason` null = closed normally. */
  | { type: 'left'; reason: LeaveReason | null }
  /** The server could not be reached (the client retries with backoff). */
  | { type: 'offline' }
  /** The player chose Leave or Menu, or Back on the profile, the settings or the shop. */
  | { type: 'menu' }
  /** The menu's Profile button. */
  | { type: 'profile' }
  /** The hub's Settings button (PHASE3 T3.7). */
  | { type: 'settings' }
  /** The hub's Shop button (PHASE3 T3.9a). */
  | { type: 'shop' }

export const initialFlow: Flow = { screen: 'menu', joining: null, error: null, closed: false }

const PHASE_SCREEN: Record<RacePhase, Screen> = {
  waiting: 'lobby',
  countdown: 'race',
  racing: 'race',
  finished: 'results',
}

/** Screens over the menu, opened from it only, where no join can start (T2.4, T3.7, T3.9a). */
export function isMenuPage(screen: Screen): boolean {
  return screen === 'profile' || screen === 'settings' || screen === 'shop'
}

/** The menu and its pages have no room: late room events and joins' leftovers are ignored. */
export function isRoomless(flow: Flow): boolean {
  return flow.screen === 'menu' || isMenuPage(flow.screen)
}

/** Emotes (keys 1–4, lobby buttons) go out only from a room: not on the menu or mid-join. */
export function canEmote(flow: Flow): boolean {
  return !isRoomless(flow) && flow.joining === null
}

/**
 * `menu → lobby → race → results`, and back: Play Again joins a new lobby, a private rematch
 * returns everyone to the same lobby (`finished → waiting`), Leave / Menu and every refusal go
 * to the menu (refusals with an inline error). Pure, so the screen rules are unit-tested.
 */
export function nextFlow(flow: Flow, ev: FlowEvent): Flow {
  switch (ev.type) {
    case 'join':
      // Joins start from the menu or the results (Play Again), never from the profile/settings.
      if (isMenuPage(flow.screen)) return flow
      return { screen: 'lobby', joining: ev.kind, error: null, closed: false }
    case 'menu':
      return initialFlow
    case 'profile':
    case 'settings':
    case 'shop':
      return flow.screen === 'menu' && !flow.joining ? { ...initialFlow, screen: ev.type } : flow
    case 'phase': {
      // Late state from a room we left, or from the old room while a new join is under way.
      if (isRoomless(flow)) return flow
      if (flow.joining && ev.phase !== 'waiting') return flow
      const screen = PHASE_SCREEN[ev.phase]
      if (screen === flow.screen && !flow.joining) return flow
      return { ...flow, screen, joining: null }
    }
    case 'left':
      if (ev.reason) return { ...initialFlow, error: ev.reason }
      // The server closes the room some seconds after the results; they stay readable.
      if (flow.screen === 'results') return { ...flow, closed: true }
      return isRoomless(flow) ? flow : initialFlow
    case 'offline':
      // Never reached a room: show the error instead of an endless "Finding a race…".
      return flow.joining ? { ...initialFlow, error: 'network' } : flow
  }
}
